class PcInspiration extends HTMLElement {
    connectedCallback() {
      this._section = this.closest('.pc-inspo');
      this._grid    = this.querySelector('[data-pc-inspo-grid]');
      this._cards   = Array.from(this.querySelectorAll('[data-pc-inspo-card]'));
      this._loadmoreRow = this.querySelector('[data-pc-loadmore-row]');
      this._loadmoreBtn = this.querySelector('[data-pc-loadmore]');
      this._totalEl     = this.querySelector('[data-pc-total]');
      this._remainingEl = this.querySelector('[data-pc-remaining]');
      this._loadmoreCt  = this.querySelector('[data-pc-loadmore-count]');
      this._lightbox    = this.querySelector('[data-pc-lightbox]');
      if (!this._grid || !this._cards.length) return;

      this._pageSize = parseInt(this._section?.dataset.pcPageSize, 10) || 6;
      this._loadStep = parseInt(this._section?.dataset.pcLoadStep, 10) || 6;
      this._visibleCount = this._pageSize;
      this._filters = { room: 'all', finish: 'all' };

      // Pre-select finish — three layers of detection:
      //   1. Liquid-supplied default (metafield or variant option) → exact slug match
      //   2. Title match (longest finish substring inside product title)
      // The first card matching the candidate finish wins.
      const liquidDefault = (this._section?.dataset.pcDefaultFinish || '').trim();
      const productTitle  = (this._section?.dataset.pcProductTitle || '').toLowerCase();

      let preselectSlug = null;
      if (liquidDefault) {
        const slug = this._slugify(liquidDefault);
        if (this._cards.some((c) => c.dataset.finishSlug === slug)) preselectSlug = slug;
      }
      if (!preselectSlug && productTitle) {
        // Longest-match wins so "European Oak Grey Felt" beats "European Oak"
        const uniqueFinishes = Array.from(new Set(this._cards.map((c) => c.dataset.finish).filter(Boolean)));
        uniqueFinishes.sort((a, b) => b.length - a.length);
        for (const f of uniqueFinishes) {
          if (productTitle.includes(f.toLowerCase())) {
            preselectSlug = this._slugify(f);
            break;
          }
        }
      }
      if (preselectSlug) {
        this._filters.finish = preselectSlug;
        this._defaultFinishSlug = preselectSlug;
      }

      this._renderOptions('room');
      this._renderOptions('finish');
      this._updateSelectValues();
      this._applyFilters();

      // Load more
      if (this._loadmoreBtn) {
        this._loadmoreBtn.addEventListener('click', () => {
          this._visibleCount += this._loadStep;
          this._applyFilters();
        });
      }

      // Close <details> dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        this.querySelectorAll('details.pc-inspo__select[open]').forEach((det) => {
          if (!det.contains(e.target)) det.open = false;
        });
      });

      // Lightbox: card image click OR read-description button click → open
      this._cards.forEach((card) => {
        const imgTrigger  = card.querySelector('[data-pc-card-trigger]');
        const readTrigger = card.querySelector('[data-pc-card-read-trigger]');
        if (imgTrigger)  imgTrigger.addEventListener('click',  () => this._openLightbox(card));
        if (readTrigger) readTrigger.addEventListener('click', () => this._openLightbox(card));
      });

      // Track the currently-expanded read button so we can reset aria-expanded on close
      this._currentReadBtn = null;

      // Lightbox: close button + ESC + backdrop click
      if (this._lightbox) {
        const closeBtn = this._lightbox.querySelector('[data-pc-lightbox-close]');
        if (closeBtn) closeBtn.addEventListener('click', () => this._closeLightbox());
        const shareBtn = this._lightbox.querySelector('[data-pc-lightbox-share]');
        if (shareBtn) shareBtn.addEventListener('click', () => this._shareLightbox());
        // Native <dialog> ESC fires 'cancel' — close on it
        this._lightbox.addEventListener('cancel', (e) => { e.preventDefault(); this._closeLightbox(); });
        // Click on backdrop (target IS the dialog)
        this._lightbox.addEventListener('click', (e) => { if (e.target === this._lightbox) this._closeLightbox(); });
      }

      // Deep-link routing: if the page loaded with #inspiration/<slug>, open that card
      // automatically once the gallery is wired up. Pattern matches both reserved IDs
      // (filename-slug) and theme-editor block IDs ("block-<id>").
      this._readHashAndOpen();
      window.addEventListener('hashchange', () => this._readHashAndOpen());
    }

    _readHashAndOpen() {
      const hash = (window.location.hash || '').replace(/^#/, '');
      const match = hash.match(/^inspiration\/(.+)$/);
      if (!match) return;
      const slug = decodeURIComponent(match[1]).toLowerCase();
      // Find the card whose data-card-slug matches (case-insensitive paranoia)
      const card = this._cards.find((c) => (c.dataset.cardSlug || '').toLowerCase() === slug);
      if (!card) return;
      // If we're already showing this card, don't re-open
      if (this._lightbox && this._lightbox.open && this._currentSlug === slug) return;
      this._openLightbox(card, { fromHash: true });
    }

    _slugify(s) { return (s || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''); }

    _uniqueValues(group) {
      const seen = new Map();
      this._cards.forEach((card) => {
        const value = card.dataset[group];
        const slug  = card.dataset[group + 'Slug'];
        if (!value || !slug) return;
        if (!seen.has(slug)) seen.set(slug, { slug, label: value, swatch: card.dataset.finishSwatch || null });
      });
      return Array.from(seen.values());
    }

    _countMatching(group, slug) {
      const otherGroup = group === 'room' ? 'finish' : 'room';
      const otherActive = this._filters[otherGroup];
      return this._cards.filter((card) => {
        if (slug !== 'all' && card.dataset[group + 'Slug'] !== slug) return false;
        if (otherActive !== 'all' && card.dataset[otherGroup + 'Slug'] !== otherActive) return false;
        return true;
      }).length;
    }

    _renderOptions(group) {
      const container = this.querySelector('[data-pc-filter-group="' + group + '"] [data-pc-options]');
      if (!container) return;
      container.innerHTML = '';
      this._appendOption(container, group, 'all', 'All', null);
      this._uniqueValues(group).forEach((v) => this._appendOption(container, group, v.slug, v.label, v.swatch));
    }

    _appendOption(container, group, slug, label, swatch) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pc-inspo__option';
      btn.dataset.pcOption = slug;
      btn.dataset.pcOptionGroup = group;
      if (this._filters[group] === slug) btn.classList.add('is-active');
      const count = this._countMatching(group, slug);
      if (count === 0 && slug !== 'all') btn.classList.add('is-empty');

      if (group === 'finish' && slug !== 'all') {
        const sw = document.createElement('span');
        sw.className = 'pc-inspo__option-swatch';
        if (swatch) sw.style.setProperty('--swatch-color', swatch);
        btn.appendChild(sw);
      }

      const lbl = document.createElement('span');
      lbl.className = 'pc-inspo__option-label';
      lbl.textContent = label;
      btn.appendChild(lbl);

      if (group === 'finish' && this._defaultFinishSlug === slug) {
        const thisTag = document.createElement('span');
        thisTag.className = 'pc-inspo__option-this';
        thisTag.textContent = 'This wood';
        btn.appendChild(thisTag);
      }

      const cnt = document.createElement('span');
      cnt.className = 'pc-inspo__option-count';
      cnt.textContent = String(count);
      btn.appendChild(cnt);

      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-empty')) return;
        this._filters[group] = slug;
        this._visibleCount = this._pageSize;
        // Close the dropdown that owns this option
        const det = btn.closest('details');
        if (det) det.open = false;
        this._renderOptions('room');
        this._renderOptions('finish');
        this._updateSelectValues();
        this._applyFilters();
      });

      container.appendChild(btn);
    }

    _updateSelectValues() {
      ['room', 'finish'].forEach((group) => {
        const trigger = this.querySelector('[data-pc-filter-group="' + group + '"] [data-pc-select-value]');
        if (!trigger) return;
        const slug = this._filters[group];
        if (slug === 'all') {
          trigger.textContent = 'All';
          return;
        }
        // Find the original label for this slug
        const match = this._uniqueValues(group).find((v) => v.slug === slug);
        trigger.textContent = match ? match.label : 'All';
      });
    }

    _applyFilters() {
      const { room, finish } = this._filters;
      let visibleMatched = 0;
      let totalMatched = 0;
      this._cards.forEach((card) => {
        const roomOk = (room === 'all') || (card.dataset.roomSlug === room);
        const finishOk = (finish === 'all') || (card.dataset.finishSlug === finish);
        const matches = roomOk && finishOk;
        if (matches) totalMatched++;
        if (matches && visibleMatched < this._visibleCount) {
          card.hidden = false;
          visibleMatched++;
        } else {
          card.hidden = true;
        }
      });
      if (this._totalEl) this._totalEl.textContent = String(totalMatched);
      const remaining = Math.max(0, totalMatched - visibleMatched);
      if (this._remainingEl) this._remainingEl.textContent = String(remaining);
      if (this._loadmoreCt)  this._loadmoreCt.textContent = String(Math.min(this._loadStep, remaining));
      if (this._loadmoreRow) this._loadmoreRow.hidden = remaining === 0;
    }

    _openLightbox(card, opts = {}) {
      if (!this._lightbox) return;

      // Sync aria-expanded on the read button — signals to crawlers that the
      // collapsed description content is now in its expanded state.
      const readBtn = card.querySelector('[data-pc-card-read-trigger]');
      if (readBtn) {
        readBtn.setAttribute('aria-expanded', 'true');
        this._currentReadBtn = readBtn;
      }

      // Track the active card so share/close can derive the slug, and update the
      // URL hash so the lightbox is deep-linkable / shareable. We DO NOT push the
      // hash when this open is itself the result of reading the hash on load —
      // that would create a no-op history entry.
      const slug = card.dataset.cardSlug || card.dataset.cardId || '';
      this._currentSlug = slug;
      if (slug && !opts.fromHash) {
        const newHash = '#inspiration/' + slug;
        if (window.location.hash !== newHash) {
          try { history.pushState(null, '', newHash); } catch (e) { /* in case of CSP / older browser */ }
        }
      }

      const img    = this._lightbox.querySelector('[data-pc-lightbox-img]');
      const fEl    = this._lightbox.querySelector('[data-pc-lightbox-finish]');
      const tEl    = this._lightbox.querySelector('[data-pc-lightbox-title]');
      const lEl    = this._lightbox.querySelector('[data-pc-lightbox-loc]');
      const src    = card.dataset.fullImg || card.querySelector('img')?.src;
      const finish = card.dataset.finish || '';
      const title  = card.querySelector('.pc-inspo-card__title')?.textContent || '';
      const loc    = card.querySelector('.pc-inspo-card__location')?.textContent || '';
      if (fEl) fEl.textContent = finish;
      if (tEl) tEl.textContent = title;
      if (lEl) lEl.textContent = loc;

      // Description — read the visually-hidden SEO description from the card and inject into lightbox
      const descTarget = this._lightbox.querySelector('[data-pc-lightbox-desc]');
      const descSrc    = card.querySelector('[data-pc-card-description]');
      if (descTarget) {
        if (descSrc && descSrc.innerHTML.trim()) {
          descTarget.innerHTML = descSrc.innerHTML;
          descTarget.hidden = false;
        } else {
          descTarget.innerHTML = '';
          descTarget.hidden = true;
        }
      }

      // Product CTA — only show when the block has a linked product
      const productBlock = this._lightbox.querySelector('[data-pc-lightbox-product]');
      const ptEl  = this._lightbox.querySelector('[data-pc-lightbox-product-title]');
      const ppEl  = this._lightbox.querySelector('[data-pc-lightbox-product-price]');
      const cta   = this._lightbox.querySelector('[data-pc-lightbox-cta]');
      const ctaLb = this._lightbox.querySelector('[data-pc-lightbox-cta-label]');
      const pUrl   = card.dataset.productUrl || '';
      const pTitle = card.dataset.productTitle || '';
      const pPrice = card.dataset.productPrice || '';
      const ctaLabel = card.dataset.ctaLabel || 'Shop this panel';
      if (productBlock) {
        if (pUrl) {
          if (ptEl) ptEl.textContent = pTitle;
          if (ppEl) { ppEl.textContent = pPrice; ppEl.hidden = !pPrice; }
          if (cta) cta.href = pUrl;
          if (ctaLb) ctaLb.textContent = ctaLabel;
          productBlock.hidden = false;
        } else {
          productBlock.hidden = true;
        }
      }

      // Set the image source — sizing is handled entirely by CSS now
      if (img) { img.src = src; img.alt = title; }

      if (typeof this._lightbox.showModal === 'function') this._lightbox.showModal();
    }

    _closeLightbox() {
      // Reset aria-expanded on the read button that opened this lightbox
      if (this._currentReadBtn) {
        this._currentReadBtn.setAttribute('aria-expanded', 'false');
        this._currentReadBtn = null;
      }
      // Clear the URL hash if it points at this gallery, so closing doesn't leave
      // a stale deep-link in the address bar. Replace rather than push so the
      // back button doesn't have a "closed lightbox" history entry.
      if (window.location.hash && window.location.hash.indexOf('#inspiration/') === 0) {
        try {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (e) { /* CSP fallback: leave the hash */ }
      }
      this._currentSlug = null;
      if (this._lightbox && this._lightbox.open) this._lightbox.close();
    }

    _shareLightbox() {
      if (!this._lightbox || !this._currentSlug) return;
      const url = window.location.origin + window.location.pathname + window.location.search + '#inspiration/' + this._currentSlug;
      const showCopiedFeedback = () => {
        const btn   = this._lightbox.querySelector('[data-pc-lightbox-share]');
        const toast = this._lightbox.querySelector('[data-pc-lightbox-toast]');
        if (btn) btn.classList.add('is-copied');
        if (toast) {
          toast.hidden = false;
          // Force a reflow so the transition runs even when toast was already display:block
          // eslint-disable-next-line no-unused-expressions
          toast.offsetHeight;
          toast.classList.add('is-visible');
        }
        clearTimeout(this._copyResetTimer);
        this._copyResetTimer = setTimeout(() => {
          if (btn) btn.classList.remove('is-copied');
          if (toast) {
            toast.classList.remove('is-visible');
            setTimeout(() => { if (toast) toast.hidden = true; }, 240);
          }
        }, 1600);
      };

      // Prefer the modern async clipboard API; fall back to a hidden textarea + execCommand
      // for older browsers (mostly Safari < 13.4 and pre-2020 Android browsers).
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(showCopiedFeedback).catch(() => this._fallbackCopy(url, showCopiedFeedback));
      } else {
        this._fallbackCopy(url, showCopiedFeedback);
      }
    }

    _fallbackCopy(text, onSuccess) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); onSuccess(); } catch (e) { /* swallow */ }
      document.body.removeChild(ta);
    }
  }
  if (!customElements.get('pc-inspiration')) { customElements.define('pc-inspiration', PcInspiration); }
